import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERM, hasPermission } from "@/lib/permissions";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Save, ArrowLeft, Info as InfoIcon, RotateCcw, Eye } from "lucide-react";
import { BoldOnlyEditor } from "@/components/BoldOnlyEditor";
import { FeedPathTag } from "@/components/FeedPathTag";
import { TemplatePreviewDialog } from "@/components/TemplatePreviewDialog";
import {
  LEGAL_TYPES,
  LEGAL_FIELDS_BY_TYPE,
  LEGAL_INFO_TEXT,
  LEGAL_FIELD_LABELS,
} from "@/lib/i18n";

export default function LegalPages({ mode = "self" }) {
  const { user, impersonating } = useAuth();
  const { t, lang } = useI18n();
  const routeParams = useParams();
  const navigate = useNavigate();

  const dealerId =
    mode === "byId" ? routeParams.id : mode === "mine" ? routeParams.dealerId : impersonating?.id || user?.dealer_id;
  // This page had no gate at all before the permission model: any
  // authenticated user who could reach a dealer could save its legal pages.
  const canEdit = hasPermission(user, dealerId, PERM.LEGAL_PAGES_EDIT);

  const [dealer, setDealer] = useState(null);
  const [draft, setDraft] = useState({});
  const [originalDraft, setOriginalDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noIncorporationTypeOpen, setNoIncorporationTypeOpen] = useState(false);
  const [tooManyBlankFieldsOpen, setTooManyBlankFieldsOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  useEffect(() => {
    if (!dealerId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await client.get(`/dealers/${dealerId}/summary`);
        setDealer(data.dealer);
        const loaded = {
          ...(data.dealer.legal_pages || {}),
          chk_legal_info_is_correct: !!data.dealer.chk_legal_info_is_correct,
        };
        setDraft(loaded);
        setOriginalDraft(loaded);
      } finally {
        setLoading(false);
      }
    })();
  }, [dealerId]);

  // Guard against stale/unmapped incorporation_type values (e.g. raw feed
  // labels that don't match any LEGAL_TYPES key) — the <Select> shows those
  // as blank/unselected, so treat them the same way here rather than as "set".
  const currentType = LEGAL_TYPES.includes(draft.incorporation_type) ? draft.incorporation_type : "";
  const visibleFields = useMemo(() => {
    const base = LEGAL_FIELDS_BY_TYPE.common;
    const extra = LEGAL_FIELDS_BY_TYPE[currentType] || [];
    return { base, extra };
  }, [currentType]);

  // Fields checked for the "at least 3 non-blank" self-confirmation rule —
  // the Common + type-specific legal fields only. Data Protection Info is
  // sourced from the feed rather than filled in by the dealer, so it
  // shouldn't count toward this dealer self-confirmation.
  const checkableFields = useMemo(
    () => [...visibleFields.base, ...visibleFields.extra],
    [visibleFields]
  );

  // Compares against the confirmation checkbox too, so unchecking it counts
  // as a change the user can revert, same as editing a textbox does.
  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(originalDraft),
    [draft, originalDraft]
  );

  const revert = () => setDraft(originalDraft);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/dealers/${dealerId}/legal-pages`, draft);
      setDealer(data);
      const saved = {
        ...(data.legal_pages || {}),
        chk_legal_info_is_correct: !!data.chk_legal_info_is_correct,
      };
      setDraft(saved);
      setOriginalDraft(saved);
      toast.success(t("dealership.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  // Editing any legal-pages field invalidates a prior self-confirmation —
  // the dealer must re-check the box after changing the data it vouches for.
  const setField = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value, chk_legal_info_is_correct: false }));
  };

  // Previews the in-memory draft (including unsaved edits) against the
  // Impressum HTML template for the currently-selected business form.
  const previewTemplate = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const fields = Object.fromEntries(
        Object.entries(draft).filter(([k, v]) => k !== "chk_legal_info_is_correct" && typeof v === "string")
      );
      const { data } = await client.post("/legal-html-templates/render-preview", {
        business_form: currentType,
        fields,
      });
      setPreviewHtml(data.html);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("templatePreview.loadFailed"));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSaveClick = () => {
    if (draft.chk_legal_info_is_correct) {
      if (!currentType) {
        setDraft({ ...draft, chk_legal_info_is_correct: false });
        setNoIncorporationTypeOpen(true);
        return;
      }
      const filledCount = checkableFields.filter((f) => String(draft[f] || "").trim() !== "").length;
      if (filledCount < 3) {
        setTooManyBlankFieldsOpen(true);
        return;
      }
    }
    save();
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl p-6 md:p-10">
        <Skeleton className="mb-6 h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  const infoText = LEGAL_INFO_TEXT[currentType]?.[lang];

  return (
    <div className="mx-auto max-w-4xl p-6 md:p-10" data-testid="legal-pages-page">
      {(mode === "byId" || mode === "mine") && (
        <button
          data-testid="back-to-details"
          onClick={() => navigate(mode === "byId" ? `/dealers/${dealerId}` : `/my-dealerships/${dealerId}`)}
          className="mb-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("legal.backToDealerDetails")}
        </button>
      )}

      <div className="mb-6">
        <p className="data-label mb-2">{t("legal.breadcrumb")}</p>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("legal.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("legal.subtitle")}</p>
        <p className="mt-1 font-mono text-xs text-muted-foreground">
          {dealer.name} · {dealer.global_id}
        </p>
      </div>

      <Card className="p-6" data-testid="legal-form">
        {/* Incorporation type */}
        <div className="mb-6 max-w-md">
          <Label className="data-label mb-1 flex items-center gap-1">
            {t("legal.incorporationType")}
            <FeedPathTag path="Dealer.Imprint.BusinessForm" />
          </Label>
          <Select
            value={currentType || undefined}
            onValueChange={(v) => setField("incorporation_type", v)}
          >
            <SelectTrigger data-testid="legal-type-select">
              <SelectValue placeholder={t("legal.select")} />
            </SelectTrigger>
            <SelectContent>
              {LEGAL_TYPES.map((typ) => (
                <SelectItem key={typ} value={typ} data-testid={`legal-type-${typ}`}>
                  {t(`legalTypes.${typ}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {currentType && (
          <>
            {infoText && (
              <div
                data-testid="legal-info-text"
                className="mb-6 flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs text-foreground"
              >
                <InfoIcon className="mt-0.5 h-4 w-4 text-primary" />
                <span>{infoText}</span>
              </div>
            )}

            <div className="mb-6">
              <p className="data-label mb-3">{t("legal.common")}</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {visibleFields.base.map((f) => (
                  <div key={f}>
                    <Label className="data-label mb-1 block">
                      {LEGAL_FIELD_LABELS[f]?.[lang] || f}
                    </Label>
                    <Input
                      data-testid={`legal-field-${f}`}
                      value={draft[f] || ""}
                      onChange={(e) => setField(f, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {visibleFields.extra.length > 0 && (
              <div className="mb-6">
                <p className="data-label mb-3">{t("legal.typeSpecific")}</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {visibleFields.extra.map((f) => (
                    <div key={f}>
                      <Label className="data-label mb-1 block">
                        {LEGAL_FIELD_LABELS[f]?.[lang] || f}
                      </Label>
                      <Input
                        data-testid={`legal-field-${f}`}
                        value={draft[f] || ""}
                        onChange={(e) => setField(f, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Data protection info reported by the feed */}
        <div className="mb-6 border-t border-border pt-6">
          <p className="data-label mb-3">{t("legal.dataProtectionInfo")}</p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Label className="data-label mb-1 flex items-center gap-1">
                {t("legal.responsiblePerson")}
                <FeedPathTag path="Dealer.DataProtectionInfo.DataProtectionResponsible" />
              </Label>
              <Input
                data-testid="legal-field-data_protection_responsible_name"
                value={draft.data_protection_responsible_name || ""}
                onChange={(e) => setField("data_protection_responsible_name", e.target.value)}
              />
            </div>
            <div>
              <Label className="data-label mb-1 flex items-center gap-1">
                {t("legal.revocationEmail")}
                <FeedPathTag path="Dealer.DataProtectionInfo.EMailRevocation" />
              </Label>
              <Input
                data-testid="legal-field-data_protection_revocation_email"
                type="email"
                value={draft.data_protection_revocation_email || ""}
                onChange={(e) => setField("data_protection_revocation_email", e.target.value)}
              />
            </div>
            <div>
              <Label className="data-label mb-1 flex items-center gap-1">
                {t("legal.noticeText")}
                <FeedPathTag path="Dealer.DataProtectionInfo.DataProtectionInfo" />
              </Label>
              <Input
                data-testid="legal-field-data_protection_notice_text"
                value={draft.data_protection_notice_text || ""}
                onChange={(e) => setField("data_protection_notice_text", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Custom text — bold-only rich text */}
        <div className="mb-6 border-t border-border pt-6">
          <p className="data-label mb-3">{t("legal.customText")}</p>
          <p className="mb-3 text-xs text-muted-foreground">
            {t("legal.customTextHintPrefix")}
            <b>{t("legal.customTextHintBold")}</b>
            {t("legal.customTextHintSuffix")}
          </p>
          <BoldOnlyEditor
            value={draft.custom_text_html || ""}
            onChange={(v) => setField("custom_text_html", v)}
            testId="legal-custom-text"
          />
          <label className="mt-4 flex items-center gap-2 text-sm" data-testid="legal-info-correct-label">
            <Checkbox
              data-testid="legal-info-correct-checkbox"
              checked={!!draft.chk_legal_info_is_correct}
              onCheckedChange={(v) => setDraft({ ...draft, chk_legal_info_is_correct: !!v })}
            />
            {t("legal.confirmCorrect")}
          </label>
        </div>

        <div className="flex justify-end gap-2">
          {currentType && (
            <Button
              data-testid="legal-preview-btn"
              variant="outline"
              onClick={previewTemplate}
              disabled={saving}
              className="gap-1.5"
            >
              <Eye className="h-3.5 w-3.5" />
              {t("legal.preview")}
            </Button>
          )}
          {isDirty && canEdit && (
            <Button
              data-testid="legal-revert-btn"
              variant="outline"
              onClick={revert}
              disabled={saving}
              className="gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t("legal.revertChanges")}
            </Button>
          )}
          {canEdit && (
            <Button
              data-testid="legal-save-btn"
              onClick={handleSaveClick}
              disabled={saving}
              className="gap-1.5"
            >
              <Save className="h-3.5 w-3.5" />
              {saving ? t("common.loading") : t("common.save")}
            </Button>
          )}
        </div>
      </Card>

      <TemplatePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        html={previewHtml}
        loading={previewLoading}
      />

      <AlertDialog open={noIncorporationTypeOpen} onOpenChange={setNoIncorporationTypeOpen}>
        <AlertDialogContent data-testid="no-incorporation-type-alert">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("legal.noIncorporationTypeTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("legal.noIncorporationTypeDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setNoIncorporationTypeOpen(false)}>
              {t("common.close")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={tooManyBlankFieldsOpen} onOpenChange={setTooManyBlankFieldsOpen}>
        <AlertDialogContent data-testid="too-many-blank-fields-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("legal.tooManyBlankFieldsTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("legal.tooManyBlankFieldsDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="too-many-blank-fields-no">{t("common.no")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="too-many-blank-fields-yes"
              onClick={() => {
                setTooManyBlankFieldsOpen(false);
                save();
              }}
            >
              {t("common.yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
